import {
  format,
  parseISO,
  isAfter,
  isBefore,
  formatDistance,
  getDaysInMonth,
  differenceInMonths,
  isPast,
  isToday as isTodayfns,
  nextDay,
  addWeeks,
  addDays,
} from 'date-fns'
import { GenericObject } from 'types/GenericObject'

const locales: GenericObject<Locale> = {
  en: require(`date-fns/locale/en-GB`).default,
  es: require(`date-fns/locale/es`).default,
}

export const dateFormatMap = {
  DAY_MONTH_YEAR: 'dd/MM/yy',
  MONTH_DAY_YEAR: 'MM/dd/yy',
  WEEKDAY: 'EEEE',
  SHORT_WEEKDAY: 'EE',
  DAY: 'd',
  MONTH: 'MMMM',
  YEAR: 'yyyy',
  MONTH_NUMBER: 'MM',
  DAY_AND_MONTH: 'dd MMMM',
  FULL_MONTH_DAY_YEAR: 'MMMM d, yyyy',
  CARDINAL_DAY_AND_MONTH: 'do MMMM',
  SHORT_WEEKDAY_AND_DAY: 'EE d',
  HOUR_AND_MINUTES: 'H:mm',
  HOUR: 'H',
  MINUTES: 'mm',
}

const isString = (value: any): value is string => {
  return typeof value === 'string'
}

export type DateFormats = keyof typeof dateFormatMap
export type DateValue = Date | string

const parse = (date: DateValue) => {
  return isString(date) ? parseISO(date) : date
}

/**
 * Given a DateValue and a date format, returns a string with the formatted date
 *
 * Example: formatDate('2014-02-11T11:30:30', 'MONTH') -> February
 */

export const formatDate = (date: DateValue, dateFormat: DateFormats, locale: string = 'en') => {
  return format(parse(date), dateFormatMap[dateFormat], { locale: locales[locale] })
}

/**
 * Returns true if the date is after the dateToCompare
 *
 * Example: isAfter('2014-02-15', '2014-02-13') -> true
 */

export const isDateAfter = (date: DateValue, dateToCompare: DateValue) => {
  return isAfter(parse(date), parse(dateToCompare))
}

/**
 * Returns true if the date is before the dateToCompare
 *
 * Example: isBefore('2014-02-13', '2014-02-15') -> true
 */

export const isDateBefore = (date: DateValue, dateToCompare: DateValue) => {
  return isBefore(parse(date), parse(dateToCompare))
}

/**
 * Returns human-readable duration string
 *
 * Example: formatDateDistance('2021-07-21T11:42:37.000Z', '2021-07-23T09:08:05.865Z') -> 2 days
 */
export const formatDateDistance = (date: DateValue, dateToCompare: DateValue) => {
  return formatDistance(parse(date), parse(dateToCompare))
}

/**
 * Returns days in month of the specified year.
 *
 * Example: getDaysInCurrentMonth(2020, 1) -> 29
 */
export const getDaysInCurrentMonth = (year: number, month: number) => {
  return getDaysInMonth(new Date(year, month - 1))
}

export const getDateDiffInYearsAndMonths = (
  date: DateValue,
  dateToCompare: DateValue = new Date().toISOString()
) => {
  const diff = differenceInMonths(parse(dateToCompare), parse(date))

  const years = Math.floor(diff / 12)
  const months = diff - years * 12

  return { years, months }
}

export const isInThePast = (date: DateValue) => {
  return isPast(parse(date))
}

export const isToday = (date: DateValue) => {
  return isTodayfns(parse(date))
}

export const dropTimeZoneString = (date: string) => {
  let value = date
  if (date.includes('T')) value = date.split('T')[0]
  if (date.includes('GMT')) value = date.split('GMT')[0]
  return new Date(value)
}

export const dropTimeZoneDate = (date: any) => {
  const value = new Date(date.toLocaleDateString().split('GMT')[0])
  return value
}

/**
 * Returns the given date if its weekday is already in selectableDaysOfWeek, otherwise
 * the nearest later date that falls on one of those weekdays.
 *
 * Example: getClosestSelectableDayOfWeek(<a Tuesday>, [1, 5]) -> that week's Friday
 */
export const getClosestSelectableDayOfWeek = (date: Date, selectableDaysOfWeek: number[]): Date => {
  if (selectableDaysOfWeek.includes(date.getDay())) return date

  for (let offset = 1; offset <= 6; offset++) {
    const candidate = addDays(date, offset)
    if (selectableDaysOfWeek.includes(candidate.getDay())) return candidate
  }

  return date
}

export const forceETStandardChargeTime = (date: Date) => {
  return new Date(format(date, 'yyyy-MM-dd') + `T09:00:00.000Z`)
}

export const getFixedDateTimeInSecondsForNextBillingDate = (
  date: Date | string,
  selectableDaysOfWeek: number[] = [5]
) => {
  if (typeof date == 'string') date = new Date(date)
  const closestSelectableDay = getClosestSelectableDayOfWeek(date, selectableDaysOfWeek)
  return forceETStandardChargeTime(closestSelectableDay)
}

export const getDateInEasternTimezone = (date: Date | string) => {
  if (typeof date == 'string') date = new Date(date)
  const offset = -360 //always set it to Eastern Standard 4am
  const newDate =
    date && date.getUTCHours() !== 0
      ? ((theDate) => new Date(theDate.getTime() - offset * 60 * 1000))(new Date(date))
      : date
  return newDate
}

export const getNextFriday = (date: Date | string) => {
  if (typeof date == 'string') date = new Date(date)
  return getNext(date, 5)
}

export const getNext = (date: Date, day: Day) => {
  return nextDay(date, day)
}

export const isTooManyWeeksInTheFuture = (date: DateValue, weekLimit: number) => {
  if (typeof date == 'string') date = new Date(date)
  const xWeeksOutDate = addWeeks(new Date(), weekLimit)
  return date > xWeeksOutDate
}

/**
 * Returns the approximated date of birth if years and months old are provided.
 * The returned date will always show the first day of the month, since it is unknown.
 *
 *
 * Example: getApproximatedDateOfBirth(10, 7) -> '2010-11-30T23:00:00.000Z' (today being 28th July 2021)
 */

export const getApproximatedDateOfBirth = (yearsOld: number, monthsOld: number = 0) => {
  const currentYear = new Date().getFullYear()
  const currentMonth = new Date().getMonth()

  const oneYearInMonths = 12
  const oneYear = 1

  const getBirthMonth = () => {
    return currentMonth > monthsOld
      ? currentMonth - monthsOld
      : currentMonth + oneYearInMonths - monthsOld
  }

  const birthYear =
    currentMonth > monthsOld ? currentYear - yearsOld : currentYear - yearsOld - oneYear

  return new Date(birthYear, getBirthMonth(), 1).toISOString()
}

export const months = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
]